import {
  ClockCountdownIcon,
  CpuIcon,
  CreditCardIcon,
  HeadsetIcon,
  PackageIcon,
  PresentationChartIcon,
  SealPercentIcon,
  SquaresFourIcon,
  TractorIcon,
  UserGearIcon,
  UsersThreeIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';

import { INTERNAL_ROLES, type PreviewFeature, type Role } from '@/types';

import { isFeatureEnabled } from './preview-features';
import { routes } from './routes';

export type NavItem = {
  key: string;
  label: string;
  icon: ReactNode;
  section?: string;
  to: string;
  roles?: Role[];
  /** Outside the release scope unless this capability is enabled (ADR-0034). */
  feature?: PreviewFeature;
};

export const nav: NavItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: <SquaresFourIcon size={16} />,
    // must match the Dashboard branch of resolveLandingRoute in src/config/routes.ts (ADR-0028)
    roles: ['super_admin', 'admin'],
    to: routes.dashboard,
  },

  // The dealer's portal (feature 120817, ADO 120818 §G): Device listing first — it is where a
  // dealer lands — then Orders, Products and Customers; Support Tickets is the shared entry below.
  // Dealer-only, so no admin sees them, and every admin entry below names its roles so no dealer
  // sees those.
  {
    key: 'dealer-devices',
    label: 'Devices',
    icon: <CpuIcon size={16} />,
    section: 'Business',
    roles: ['dealer'],
    feature: 'dealers',
    to: routes.dealerPortalDevices,
  },
  {
    key: 'dealer-orders',
    label: 'Orders',
    icon: <PackageIcon size={16} />,
    section: 'Business',
    roles: ['dealer'],
    feature: 'dealers',
    to: routes.dealerPortalOrders,
  },
  {
    key: 'dealer-products',
    label: 'Products',
    icon: <TractorIcon size={16} />,
    section: 'Business',
    roles: ['dealer'],
    feature: 'dealers',
    to: routes.dealerPortalProducts,
  },
  {
    key: 'dealer-customers',
    label: 'Customers',
    icon: <UsersThreeIcon size={16} />,
    section: 'Business',
    roles: ['dealer'],
    feature: 'dealers',
    to: routes.dealerPortalCustomers,
  },

  {
    key: 'orders',
    label: 'Orders',
    icon: <PackageIcon size={16} />,
    section: 'Business',
    // must match ORDERS_ROLES in src/features/orders/constants.ts
    // (config cannot import from feature under eslint-plugin-boundaries)
    roles: ['super_admin', 'admin', 'operational_associate'],
    to: routes.orders,
  },
  {
    key: 'products',
    label: 'Products',
    icon: <TractorIcon size={16} />,
    section: 'Business',
    roles: ['admin', 'super_admin'],
    to: routes.products,
  },
  {
    key: 'customers',
    label: 'Customers',
    icon: <UsersThreeIcon size={16} />,
    section: 'Business',
    // must match CUSTOMERS_ROLES in src/features/customers/constants.ts
    // (config cannot import from feature under eslint-plugin-boundaries)
    roles: ['super_admin', 'admin', 'operational_associate'],
    to: routes.customers,
  },

  {
    key: 'requests',
    label: 'Request',
    icon: <WrenchIcon size={16} />,
    section: 'Support',
    // must match REQUESTS_ROLES in src/features/requests/constants.ts
    // (config cannot import from feature under eslint-plugin-boundaries)
    roles: ['super_admin', 'admin', 'operational_associate'],
    to: routes.requests,
  },
  {
    key: 'support',
    label: 'Support Tickets',
    icon: <HeadsetIcon size={16} />,
    section: 'Support',
    feature: 'support',
    to: routes.support,
  },

  {
    key: 'payments',
    label: 'Payments',
    icon: <CreditCardIcon size={16} />,
    section: 'Finances',
    // must match PAYMENTS_ROLES in src/features/payments/constants.ts
    // (config cannot import from feature under eslint-plugin-boundaries)
    roles: ['super_admin', 'admin', 'operational_associate'],
    to: routes.payments,
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: <PresentationChartIcon size={16} />,
    section: 'Finances',
    // must match REPORTS_ROLES in src/features/reports/constants.ts
    // (config cannot import from feature under eslint-plugin-boundaries)
    roles: ['super_admin', 'admin'],
    to: routes.reports,
  },

  {
    key: 'activity-log',
    label: 'Activity Log',
    icon: <ClockCountdownIcon size={16} />,
    section: 'Admin',
    feature: 'activity-log',
    // A dealer sees no admin navigation (ADO 120818 §G).
    roles: INTERNAL_ROLES,
    to: routes.activityLog,
  },
  {
    key: 'discounts',
    label: 'Discounts',
    icon: <SealPercentIcon size={16} />,
    section: 'Admin',
    // must match DISCOUNTS_ROLES in src/features/discounts/constants.ts
    // (config cannot import from feature under eslint-plugin-boundaries)
    roles: ['admin', 'super_admin'],
    to: routes.discounts,
  },
  {
    key: 'admin-users',
    // Feature 118478: with `dealers` on this is the User Management hub, which Customer Support
    // reaches for the Dealers tab (grilling Q3). Getters, not values: the flag is read at render.
    get label() {
      return isFeatureEnabled('dealers') ? 'User Management' : 'Internal Users';
    },
    icon: <UserGearIcon size={16} />,
    section: 'Admin',
    // must match ADMIN_USERS_ROLES / DEALERS_ROLES in src/features/{admin-users,dealers}/constants.ts
    get roles(): Role[] {
      return isFeatureEnabled('dealers')
        ? ['admin', 'super_admin', 'operational_associate']
        : ['admin', 'super_admin'];
    },
    to: routes.adminUsers,
  },
];
